import { SearchBy, SearchType } from '@cube3/state/src/redux/ducks/search';
import { QueryStructure, SearchFilterQueries } from '../../types';

const searchByLookup: { [key in SearchBy]: string } = {
  all: 'all',
  'metadata-value': 'metadata_values',
  name: 'display_name',
  tags: 'tags',
  'contract-value': 'contract_values',
  favorites: 'display_name'
};

const ASSETS = ['video', 'audio', 'image', 'document', 'url'];

export const getSearchTypeOptions = (searchBy: SearchBy): SearchType[] => {
  switch (searchBy) {
    case 'name':
      return ['asset', 'folder', 'project', 'contract'];
    case 'favorites':
      return ['asset', 'folder', 'project'];
    case 'metadata-value':
      return ['asset', 'project'];
    case 'contract-value':
      return ['asset', 'project', 'contract'];
    case 'tags':
      return ['asset', 'folder'];
    default:
      return null;
  }
};

const getSearchBy = (type: SearchType): SearchBy[] => {
  switch (type) {
    case 'project':
      return ['contract-value', 'metadata-value', 'name'];
    case 'folder':
      return ['name', 'tags'];
    case 'contract':
      return ['contract-value', 'name'];
    case 'url':
      return ['name', 'tags'];
    default:
      return ['contract-value', 'metadata-value', 'name', 'tags'];
  }
};

/** returns `[type].[searchBy]` by default, etc: `folder.display_name`
 *  - if the selected type is an `asset` type, returns `asset.[searchBy]`
 */
const makeQeuryByString = (searchBy: SearchBy, type: SearchType) => {
  const ty = ASSETS.includes(type) ? 'asset' : type;
  if (ty === 'contract' && searchBy === 'contract-value') {
    return 'contract.values';
  }
  return `${ty}.${searchByLookup[searchBy]}`;
};

/** - if 'any' is selected, should merge all the `queryBy` options into a string, separated by `,`
 * - otherwise, return an empty string
 */
const mergeQueryBy = (searchBy: SearchBy, type: SearchType) => {
  const supportTypes = getSearchTypeOptions(searchBy);
  // don't send `queryBy`
  if (!supportTypes && type === 'all') return '';
  if (supportTypes && type === 'all') {
    return supportTypes.map((t) => makeQeuryByString(searchBy, t)).join(',');
  }
  return makeQeuryByString(searchBy, type);
};

/** Creates a valid query objects and leaves out keys that would otherwise break the API request because of empty values (bad request).
 * Examples:
 * - search for all items by `display_name`:
 *      - search[query]=foo (no queryBy)
 * - search for videos by `display_name`:
 *      - filter[asset.type]=video&filter[queryBy]=asset.display_name&search[query]=foo
 * - search for urls:
 *      - filter[queryBy]=asset.url&search[query]=foo
 * - search for tags:
 *      - filter[queryBy]=asset.tags,folder.tags&search[query]=foo
 * - search for all favorited items:
 *      - filter[favorited]=1&filter[queryBy]=asset.display_name,folder.display_name
 */

export const makeQueryParams = ({
  display_name = '',
  searchIn,
  type,
  searchBy
}: SearchFilterQueries): QueryStructure => {
  const queryParams: QueryStructure = {
    filter: {},
    search: {
      query: display_name.trim()
    }
  };

  if (searchIn) {
    queryParams.filter.ancestor = searchIn;
  }

  if (searchBy === 'all' && type === 'all') {
    return queryParams;
  }

  if (searchBy) {
    if (searchBy === 'all') {
      queryParams.filter.queryBy = getSearchBy(type)
        .map((str) => makeQeuryByString(str, type))
        .join(',');
    } else {
      if (searchBy === 'favorites') {
        queryParams.filter.favorited = '1';
      }
      const queryBy = mergeQueryBy(searchBy, type);
      if (queryBy) {
        queryParams.filter.queryBy = queryBy;
      }
    }
  }

  if (type && type !== 'all') {
    if (ASSETS.includes(type)) {
      queryParams.filter['asset.type'] = type;
    }
  }

  return queryParams;
};
